import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { dailyTotals, enabledSessions, reconcileMilkDay, sessionTotals } from '@dairy/domain';
import type { MilkSession } from '@dairy/shared';
import { useIsOwner, useSettings } from '@/auth/useSession';
import { Badge, Card, CardTitle, PageHeader, Table, Td, Th, buttonVariants } from '@/components/ui';
import { useHerd } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { useMilkRecords, useMilkSales, useUsage } from './useMilk';

/** Screen 5 — Day detail (MR-6, IN-6): per session and per cow, withheld reasons, late-entry marks, saleable vs sold. */
export function MilkDayPage() {
  const { date = '' } = useParams();
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const isOwner = useIsOwner();
  const herd = useHerd(date);
  const records = useMilkRecords(date, date) ?? [];
  const usage = useUsage(date, date) ?? [];
  const sales = useMilkSales(date, date) ?? [];
  const tot = dailyTotals(records, usage, date);
  const bySession = sessionTotals(records, date);
  const sessions = enabledSessions(s).map((x) => x.session);
  const sold = sales.reduce((a, x) => a + (x.milkMl ?? 0), 0);
  const rec = reconcileMilkDay(tot.saleableMl, sold, s.unaccountedMilkThreshold);
  const cowIds = [...new Set(records.map((r) => r.animalId))].sort((a, b) =>
    (herd.byId.get(a)?.animal.tag ?? '').localeCompare(herd.byId.get(b)?.animal.tag ?? '', 'en', { numeric: true }),
  );
  return (
    <div className="space-y-4">
      <PageHeader title={f.date(date)} />
      <div className="flex flex-wrap gap-2">
        {sessions.map((ses) => (
          <Link key={ses} to={`/milk/session?date=${date}&session=${ses}`} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            {t('milk.editSession', { session: f.session(ses) })}
          </Link>
        ))}
        <Link to={`/milk/usage?date=${date}`} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          {t('milk.editUsage')}
        </Link>
      </div>
      <Card>
        <CardTitle>{t('milk.daySummary')}</CardTitle>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
          <Item label={t('milk.produced')} value={f.liters(tot.producedMl)} />
          <Item label={t('milk.withheld')} value={f.liters(tot.withheldMl)} />
          <Item label={t('milk.usageCalves')} value={f.liters(tot.calfMl)} />
          <Item label={t('milk.usageHome')} value={f.liters(tot.homeMl)} />
          <Item label={t('milk.usageSpoiled')} value={f.liters(tot.spoiledMl)} />
          <Item label={t('milk.saleable')} value={f.liters(tot.saleableMl)} strong />
          <Item label={t('milk.sold')} value={f.liters(sold)} />
          {sessions.map((ses) => (
            <Item key={ses} label={f.session(ses)} value={f.liters(bySession[ses])} />
          ))}
        </dl>
        {tot.saleableMl < 0 ? <Badge tone="red" className="mt-3">{t('milk.negativeSaleable')}</Badge> : null}
        {tot.producedMl > 0 && rec.flag !== 'OK' ? (
          <Badge tone="amber" className="mt-3">
            {rec.flag === 'OVERSOLD' ? t('milk.oversold') : t('milk.unaccounted', { liters: f.liters(rec.unaccountedMl) })}
          </Badge>
        ) : null}
      </Card>
      <Card>
        <CardTitle>{t('milk.perCow')}</CardTitle>
        <Table>
          <thead>
            <tr>
              <Th>{t('animals.tag')}</Th>
              {sessions.map((ses) => (
                <Th key={ses} className="text-right">
                  {f.session(ses)}
                </Th>
              ))}
              <Th className="text-right">{t('common.total')}</Th>
            </tr>
          </thead>
          <tbody>
            {cowIds.map((id) => {
              const mine = records.filter((r) => r.animalId === id);
              return (
                <tr key={id}>
                  <Td className="font-semibold">
                    <Link to={`/animals/${id}`}>{herd.byId.get(id)?.animal.tag ?? '?'}</Link>
                  </Td>
                  {sessions.map((ses) => {
                    const r = mine.find((x) => x.session === (ses as MilkSession));
                    return (
                      <Td key={ses} className="text-right tabular-nums">
                        {r ? f.liters(r.ml) : '—'}
                        {r?.withheld ? <div className="text-xs text-amber-800">{t(`milk.withheldReason.${r.withheldReason ?? 'OTHER'}`)}</div> : null}
                        {r?.withheldNote && !r.withheld ? <div className="text-xs text-stone-500">{r.withheldNote}</div> : null}
                        {isOwner && r?.lateEntry ? <div className="text-xs text-red-700">{t('milk.late')}</div> : null}
                      </Td>
                    );
                  })}
                  <Td className="text-right font-semibold tabular-nums">{f.liters(mine.reduce((a, r) => a + r.ml, 0))}</Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}

function Item({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-sm text-stone-600">{label}</dt>
      <dd className={strong ? 'text-lg font-bold tabular-nums' : 'font-semibold tabular-nums'}>{value}</dd>
    </div>
  );
}
